import React, { useEffect, useState } from "react";
import { getAccuracyReport } from "@/lib/apiClient";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Loader2, Target } from "lucide-react";
import { cn } from "@/lib/utils";

const pct = (v) => (v == null ? "—" : `${v}%`);
const tone = (v) => (v == null ? "text-muted-foreground" : v >= 95 ? "text-emerald-500" : v >= 85 ? "text-amber-500" : "text-red-500");

const Stat = ({ label, value, sub, testId }) => (
  <div className="rounded-lg border border-border bg-muted/20 p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn("mt-1 font-mono text-2xl font-bold", tone(value))}>{pct(value)}</div>
    <div className="text-[11px] text-muted-foreground">{sub}</div>
  </div>
);

export const AccuracyReport = () => {
  const [months, setMonths] = useState(6);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); getAccuracyReport(months).then(setD).catch(() => setD(false)); }, [months]);

  return (
    <section className="rounded-xl border border-border bg-card p-5" data-testid="accuracy-report-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><Target className="h-5 w-5 text-primary" /> Accuracy report</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            <b>First-time-right</b> = reviewed invoices where the AI's supplier, dates, totals <i>and</i> every GL code were accepted without a single correction.
          </p>
        </div>
        <div className="flex rounded-md border border-border p-0.5 text-xs" data-testid="accuracy-range">
          {[3, 6, 12].map((m) => (
            <button key={m} data-testid={`accuracy-range-${m}`} onClick={() => setMonths(m)} className={cn("rounded px-2.5 py-1", months === m ? "bg-muted font-medium" : "text-muted-foreground")}>{m} mo</button>
          ))}
        </div>
      </div>

      {d === null && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
      {d === false && <div className="py-6 text-sm text-red-500">Could not load the accuracy report.</div>}
      {d && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="accuracy-totals">
            <Stat testId="accuracy-ftr" label="First-time-right" value={d.totals.ftr_pct} sub={`${d.totals.first_time_right} of ${d.totals.reviewed} reviewed invoices`} />
            <Stat testId="accuracy-header" label="Header fields" value={d.totals.header_accuracy_pct} sub="supplier · number · dates · totals untouched" />
            <Stat testId="accuracy-gl" label="GL coding" value={d.totals.gl_accuracy_pct} sub="line items kept the suggested code" />
            <Stat testId="accuracy-auto" label="Straight-through" value={d.totals.auto_coded_pct} sub="invoices that needed no human at all" />
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <div className="text-xs text-muted-foreground mb-2">First-time-right by month · target 95%</div>
              <div className="h-44" data-testid="accuracy-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.months} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "short" })} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                             formatter={(v, _n, p) => [`${v ?? "—"}% (${p.payload.first_time_right}/${p.payload.reviewed})`, "First-time-right"]} />
                    <ReferenceLine y={95} stroke="hsl(var(--primary))" strokeDasharray="4 4" />
                    <Bar dataKey="ftr_pct" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Most-corrected fields</div>
                <ul className="space-y-0.5" data-testid="accuracy-fields">
                  {d.field_corrections.filter((f) => f.count > 0).slice(0, 4).map((f) => (
                    <li key={f.field} className="flex justify-between"><span className="capitalize">{f.field.replace("_", " ")}</span><span className="font-mono">{f.count}</span></li>
                  ))}
                  {d.field_corrections.every((f) => f.count === 0) && <li className="text-muted-foreground">No header corrections in this period</li>}
                </ul>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Suppliers needing the most fixes</div>
                <ul className="space-y-0.5" data-testid="accuracy-suppliers">
                  {d.top_corrected_suppliers.map((s) => <li key={s.supplier} className="flex justify-between"><span className="truncate">{s.supplier}</span><span className="font-mono">{s.count}</span></li>)}
                  {d.top_corrected_suppliers.length === 0 && <li className="text-muted-foreground">None</li>}
                </ul>
              </div>
              <div className="text-[11px] text-muted-foreground" data-testid="accuracy-learned">
                D20 has learned {d.aliases_learned} supplier name{d.aliases_learned === 1 ? "" : "s"} and {d.corrections_learned} field correction{d.corrections_learned === 1 ? "" : "s"} so far.
              </div>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto" data-testid="accuracy-table">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="[&>th]:px-3 [&>th]:py-2"><th>Month</th><th className="text-right">Invoices</th><th className="text-right">Reviewed</th><th className="text-right">First-time-right</th><th className="text-right">Header</th><th className="text-right">GL coding</th><th className="text-right">Straight-through</th></tr>
              </thead>
              <tbody>
                {d.months.map((m) => (
                  <tr key={m.month} className="border-t border-border/60 [&>td]:px-3 [&>td]:py-1.5 font-mono text-xs" data-testid={`accuracy-row-${m.month}`}>
                    <td className="font-sans">{new Date(`${m.month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</td>
                    <td className="text-right">{m.invoices}</td><td className="text-right">{m.reviewed}</td>
                    <td className={cn("text-right", tone(m.ftr_pct))}>{pct(m.ftr_pct)}</td>
                    <td className="text-right">{pct(m.header_accuracy_pct)}</td><td className="text-right">{pct(m.gl_accuracy_pct)}</td><td className="text-right">{pct(m.auto_coded_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};
